import React, {
  useState,
  useCallback,
  useRef,
  useLayoutEffect,
} from 'react';
import ReactDOM from 'react-dom';
import { Card } from 'antd';
import { useSelector } from 'react-redux';
import { TransferForm } from '../../components/Transfer/TransferForm';
import { TitleBreadcrumbs } from '../../uiComponents/TitleBreadcrumbs';
import { MinMaxSidebar } from '../../uiComponents/MinMaxSidebar';
import { FixedMenuButton } from '../../uiComponents/FixedMenuButton';
import styles from './Transfer.module.css';

// must match .sidebarFloating top in Transfer.module.css
const SIDEBAR_TOP_OFFSET = 190;
const SIDEBAR_BOTTOM_GAP = 32;

export const Transfer = () => {
  const {
    fullname,
    role,
    branchId,
    privileges: { transfers = [] },
  } = useSelector((state) => state.userAuth);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 991);
  const [sidebarMetrics, setSidebarMetrics] = useState({
    left: 0,
    width: 0,
    maxHeight: 0,
  });
  const sidebarPanelRef = useRef(null);

  useLayoutEffect(() => {
    if (!sidebarOpen || !sidebarPanelRef.current) return undefined;

    const updateSidebarLayout = () => {
      if (!sidebarPanelRef.current) return;

      const rect = sidebarPanelRef.current.getBoundingClientRect();
      const footer = document.querySelector('.ant-layout-footer');
      const footerTop = footer
        ? footer.getBoundingClientRect().top
        : window.innerHeight;
      const availableBottom = Math.min(footerTop, window.innerHeight);
      const maxHeight = Math.max(
        200,
        availableBottom - SIDEBAR_TOP_OFFSET - SIDEBAR_BOTTOM_GAP,
      );

      setSidebarMetrics((previous) =>
        previous.left === rect.left &&
        previous.width === rect.width &&
        previous.maxHeight === maxHeight
          ? previous
          : { left: rect.left, width: rect.width, maxHeight },
      );
      setIsMobile(window.innerWidth <= 991);
    };

    updateSidebarLayout();
    window.addEventListener('resize', updateSidebarLayout);
    window.addEventListener('scroll', updateSidebarLayout, true);

    const scrollContainer = document.querySelector('main.ant-layout-content');
    scrollContainer?.addEventListener('scroll', updateSidebarLayout);

    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(updateSidebarLayout);
      observer.observe(sidebarPanelRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateSidebarLayout);
      window.removeEventListener('scroll', updateSidebarLayout, true);
      scrollContainer?.removeEventListener('scroll', updateSidebarLayout);
      if (observer) observer.disconnect();
    };
  }, [sidebarOpen]);

  const handleOnClick = useCallback(() => {
    setSidebarOpen(!sidebarOpen);
  }, [sidebarOpen]);

  return (
    <>
      <Card bordered={true} title={<TitleBreadcrumbs />} className="gx-card">
        <div className={styles.container}>
          <div
            className={styles.formPanel}
            style={{ flex: sidebarOpen ? 5 : 1 }}
          >
            <TransferForm
              logedUserName={fullname}
              role={role}
              loggedBranchId={branchId}
              privileges={transfers}
            />
          </div>
          {sidebarOpen && (
            <div ref={sidebarPanelRef} className={styles.sidebarPanel}>
              {isMobile && (
                <div className={styles.sidebarScrollArea}>
                  <MinMaxSidebar setSidebarOpen={setSidebarOpen} />
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
      {sidebarOpen && !isMobile && sidebarMetrics.width > 0 &&
        ReactDOM.createPortal(
          <div
            className={styles.sidebarFloating}
            style={{
              left: `${sidebarMetrics.left}px`,
              width: `${sidebarMetrics.width}px`,
            }}
          >
            <div
              className={styles.sidebarScrollArea}
              style={{ maxHeight: `${sidebarMetrics.maxHeight}px` }}
            >
              <MinMaxSidebar setSidebarOpen={setSidebarOpen} />
            </div>
          </div>,
          document.body,
        )}
      <FixedMenuButton onClick={handleOnClick} />
    </>
  );
};
